import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check, ArrowRight, ArrowLeft, Sparkles,
  Mic, Gamepad2, Briefcase, DollarSign, GraduationCap, Dumbbell, Sofa, Video, Layers,
  Eye, Users, Handshake, Timer, Rocket,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';

/* ---------- Static option catalogues (labels resolved via t()) ---------- */
const CONTENT_TYPES = [
  { id: 'podcast', labelKey: 'onb.podcast', icon: Mic },
  { id: 'gaming', labelKey: 'onb.gaming', icon: Gamepad2 },
  { id: 'business', labelKey: 'onb.business', icon: Briefcase },
  { id: 'finance', labelKey: 'onb.finance', icon: DollarSign },
  { id: 'education', labelKey: 'onb.education', icon: GraduationCap },
  { id: 'fitness', labelKey: 'onb.fitness', icon: Dumbbell },
  { id: 'lifestyle', labelKey: 'onb.lifestyle', icon: Sofa },
  { id: 'vlog', labelKey: 'onb.vlog', icon: Video },
  { id: 'other', labelKey: 'onb.other', icon: Layers },
];

const PLATFORMS = [
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube_shorts', label: 'YouTube Shorts' },
  { id: 'instagram_reels', label: 'Instagram Reels' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'x', label: 'X' },
];

const GOALS = [
  { id: 'views', labelKey: 'onb.goal.views', icon: Eye },
  { id: 'subs', labelKey: 'onb.goal.subs', icon: Users },
  { id: 'clients', labelKey: 'onb.goal.clients', icon: Handshake },
  { id: 'time', labelKey: 'onb.goal.time', icon: Timer },
  { id: 'brand', labelKey: 'onb.goal.brand', icon: Rocket },
];

const CADENCE = [
  { id: '1-3', labelKey: 'onb.cadence.1-3' },
  { id: '4-7', labelKey: 'onb.cadence.4-7' },
  { id: '8-15', labelKey: 'onb.cadence.8-15' },
  { id: '15+', labelKey: 'onb.cadence.15+' },
];

/* ---------- Card primitives ---------- */
const OptionCard = ({ active, onClick, icon: Icon, label, testid, multi }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`group relative p-4 rounded-xl border text-left transition-all duration-200 flex items-center gap-3 ${
      active
        ? 'border-[#D4FF00] bg-[#D4FF00]/[0.08] shadow-[0_0_25px_-10px_rgba(212,255,0,0.6)]'
        : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]'
    }`}
  >
    {Icon && (
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition ${
        active ? 'bg-[#D4FF00] text-black' : 'bg-white/5 text-zinc-400 group-hover:text-white'
      }`}>
        <Icon className="w-4 h-4" />
      </div>
    )}
    <span className={`text-sm font-medium ${active ? 'text-white' : 'text-zinc-300'}`}>{label}</span>
    {multi && (
      <span className={`ml-auto w-5 h-5 rounded-md border-2 flex items-center justify-center transition ${
        active ? 'border-[#D4FF00] bg-[#D4FF00]' : 'border-white/20'
      }`}>
        {active && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
      </span>
    )}
  </button>
);

const PANEL_ANIM = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

/* ---------- Main component ---------- */
const Onboarding = () => {
  const navigate = useNavigate();
  const { t, lang } = useT();
  const { user } = useAuth();

  // 0 = welcome, 1 = type, 2 = platforms, 3 = goal, 4 = cadence, 5 = done
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    content_type: null,
    platforms: [],
    goal: null,
    cadence: null,
  });

  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const togglePlatform = (id) => setProfile((p) => ({
    ...p,
    platforms: p.platforms.includes(id) ? p.platforms.filter((x) => x !== id) : [...p.platforms, id],
  }));

  const TOTAL_QUESTIONS = 4; // steps 1..4
  const questionIndex = Math.max(0, Math.min(step - 1, TOTAL_QUESTIONS));
  const showProgress = step >= 1 && step <= TOTAL_QUESTIONS;

  const canNext = () => {
    if (step === 1) return !!profile.content_type;
    if (step === 2) return profile.platforms.length > 0;
    if (step === 3) return !!profile.goal;
    if (step === 4) return !!profile.cadence;
    return true;
  };

  const persistProfile = async () => {
    if (!user) return; // guarded route anyway
    try {
      setSaving(true);
      await api.put('/account/profile', profile);
    } catch (e) {
      // Silent — onboarding must never block the user. We surface a soft toast.
      toast.error(lang === 'fr'
        ? "Impossible de sauvegarder tes préférences, on continue quand même."
        : "Couldn't save your preferences, moving on.");
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    if (step === 4) {
      await persistProfile();
      setStep(5);
      return;
    }
    if (step === 5) {
      navigate('/dashboard');
      return;
    }
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const skip = () => navigate('/dashboard');

  /* ------- Screen renderers ------- */
  const renderWelcome = () => (
    <div className="text-center py-4">
      <div className="w-16 h-16 rounded-2xl bg-[#D4FF00] mx-auto flex items-center justify-center mb-6 shadow-[0_0_40px_-10px_rgba(212,255,0,0.7)]">
        <Sparkles className="w-8 h-8 text-black" strokeWidth={2.5} />
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight" data-testid="onboard-welcome-title">{t('onb.welcome.title')}</h1>
      <p className="text-zinc-400 mt-3 text-base">{t('onb.welcome.sub')}</p>
    </div>
  );

  const renderType = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="onboard-step-type">
      {CONTENT_TYPES.map((c) => (
        <OptionCard
          key={c.id}
          active={profile.content_type === c.id}
          onClick={() => setField('content_type', c.id)}
          icon={c.icon}
          label={t(c.labelKey)}
          testid={`onboard-type-${c.id}`}
        />
      ))}
    </div>
  );

  const renderPlatforms = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="onboard-step-platforms">
      {PLATFORMS.map((p) => (
        <OptionCard
          key={p.id}
          active={profile.platforms.includes(p.id)}
          onClick={() => togglePlatform(p.id)}
          label={p.label}
          testid={`onboard-platform-${p.id}`}
          multi
        />
      ))}
    </div>
  );

  const renderGoal = () => (
    <div className="grid grid-cols-1 gap-3" data-testid="onboard-step-goal">
      {GOALS.map((g) => (
        <OptionCard
          key={g.id}
          active={profile.goal === g.id}
          onClick={() => setField('goal', g.id)}
          icon={g.icon}
          label={t(g.labelKey)}
          testid={`onboard-goal-${g.id}`}
        />
      ))}
    </div>
  );

  const renderCadence = () => (
    <div className="grid grid-cols-2 gap-3" data-testid="onboard-step-cadence">
      {CADENCE.map((c) => (
        <OptionCard
          key={c.id}
          active={profile.cadence === c.id}
          onClick={() => setField('cadence', c.id)}
          label={t(c.labelKey)}
          testid={`onboard-cadence-${c.id}`}
        />
      ))}
    </div>
  );

  const renderDone = () => (
    <div className="text-center py-6" data-testid="onboard-step-done">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 240, damping: 18 }}
        className="w-20 h-20 rounded-full bg-[#D4FF00] mx-auto flex items-center justify-center mb-6 shadow-[0_0_60px_-10px_rgba(212,255,0,0.8)]"
      >
        <Check className="w-10 h-10 text-black" strokeWidth={3} />
      </motion.div>
      <h2 className="font-heading text-3xl font-black tracking-tight">{t('onb.done.title')}</h2>
      <p className="text-zinc-400 mt-3">{t('onb.done.sub')}</p>
    </div>
  );

  const HEADERS = {
    1: { title: t('onb.type.title'), sub: t('onb.type.sub') },
    2: { title: t('onb.platforms.title'), sub: t('onb.platforms.sub') },
    3: { title: t('onb.goal.title'), sub: t('onb.goal.sub') },
    4: { title: t('onb.cadence.title'), sub: t('onb.cadence.sub') },
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 sm:px-6 py-10 dot-grid">
      <div className="w-full max-w-2xl" data-testid="onboarding">

        {/* Progress bar */}
        {showProgress && (
          <div className="flex items-center gap-1.5 mb-8" data-testid="onboard-progress">
            {Array.from({ length: TOTAL_QUESTIONS }, (_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                  i <= questionIndex ? 'bg-[#D4FF00]' : 'bg-white/10'
                }`}
              />
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            {...PANEL_ANIM}
            className="p-8 sm:p-10 rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl"
          >
            {/* Header for question steps */}
            {step >= 1 && step <= 4 && (
              <div className="mb-8">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#D4FF00] font-mono" data-testid="onboard-step-indicator">
                  {t('onb.step')} {step} / {TOTAL_QUESTIONS}
                </span>
                <h1 className="font-heading text-2xl sm:text-3xl font-black mt-3 tracking-tight">
                  {HEADERS[step].title}
                </h1>
                <p className="text-zinc-400 mt-2 text-sm">{HEADERS[step].sub}</p>
              </div>
            )}

            {step === 0 && renderWelcome()}
            {step === 1 && renderType()}
            {step === 2 && renderPlatforms()}
            {step === 3 && renderGoal()}
            {step === 4 && renderCadence()}
            {step === 5 && renderDone()}

            {/* Footer actions */}
            <div className="mt-10 flex items-center justify-between gap-3">
              {step > 0 && step < 5 ? (
                <Button
                  variant="ghost"
                  onClick={back}
                  className="text-zinc-400 hover:text-white"
                  data-testid="onboard-back"
                >
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('onb.back')}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={skip}
                  className="text-zinc-500 hover:text-white"
                  data-testid="onboard-skip"
                >
                  {t('onb.skip')}
                </Button>
              )}
              <Button
                onClick={next}
                disabled={!canNext() || saving}
                className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold px-6 sm:px-8 h-11 rounded-xl transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4FF00]/25 disabled:opacity-40 disabled:hover:transform-none disabled:cursor-not-allowed"
                data-testid="onboard-next"
              >
                {step === 0 && <>{t('onb.welcome.cta')} <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                {step >= 1 && step <= 3 && <>{t('onb.next')} <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                {step === 4 && (saving
                  ? (lang === 'fr' ? 'Enregistrement…' : 'Saving…')
                  : <>{t('onb.next')} <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
                {step === 5 && t('onb.start')}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Onboarding;
